<template>
  <!--
    flex-1 rather than relying on h-full alone: a percentage height does not resolve
    against a flex-sized parent (measured: h-full computed to 581px inside an 812px
    parent), which left the bottom-pinned CTAs with no space to push into.
  -->
  <div
    class="mx-auto w-full h-full flex flex-1 flex-col"
    :data-testid="props.dataTestid"
    :class="{'max-w-md':compact}"
    @reset-form="resetForm"
  >
    <!--
      Second-factor challenge. Takes over the whole card once Descope confirms this
      account has a verified phone, and is gated on feature_DescopeMFA alone — this screen
      is the LEGACY login form, shown when feature_DescopeAuthFlows is off, so MFA has to
      work here independently of the Descope login flow.
    -->
    <div
      v-if="mfaChallengeActive"
      class="h-full flex flex-1 flex-col md:h-auto md:flex-none md:block"
      :class="inAuthModal ? '' : 'pb-safe'"
    >
      <V2MoleculesOtpSendBlocked
        v-if="mfaSendLockout.visible.value"
        :destination="maskedPhone"
        :seconds-remaining="mfaSendLockout.secondsRemaining.value"
        :can-retry="mfaSendLockout.canRetry.value"
        data-testid="auth-legacy-sms-otp-send-blocked"
        @retry="mfaSendLockout.retry(handleMfaResend)"
        @not-now="cancelMfaChallenge"
      />
      <V2MoleculesOtpVerification
        v-else
        :pin-cta="!inAuthModal"
        auto-submit-on-complete
        class="flex-1 md:flex-none"
        :destination="maskedPhone"
        expiry-label="It expires in 5 minutes."
        :error="mfaError"
        :is-submitting="isMfaSubmitting || mfaVerified"
        data-testid="auth-legacy-sms-otp-verification"
        @verify="handleMfaVerify"
        @resend="handleMfaResend"
        @cant-access-click="startAccountRecovery"
      >
        <template #back>
          <button
            id="auth_legacy_sms_back_btn"
            type="button"
            data-testid="auth-legacy-sms-back-btn"
            class="mb-6 inline-flex items-center gap-1 text-sm font-semibold text-semantic-primary-default underline"
            :disabled="isMfaSubmitting"
            @click="cancelMfaChallenge"
          >
            <V2IconsArrow
              direction="left"
              fill="currentColor"
              width="16"
              height="16"
            />
            Back
          </button>
        </template>
      </V2MoleculesOtpVerification>
    </div>
    <!--
      Inline SMS enrolment. Only used inside the auth modal, where redirecting to
      /dashboard/setup-mfa/ would eject the user from the quote funnel they are part-way
      through. Gated on feature_DescopeMFA like every other MFA surface.
    -->
    <div v-else-if="mfaSetupActive" class="h-full flex flex-1 flex-col">
      <V2OrganismAuthMfaEnrollment
        :login-id="model.email"
        :refresh-token="mfaSetupRefreshToken"
        :initial-phone="mfaSetupInitialPhone"
        embedded
        :allow-skip="mfaSetupAllowSkip"
        :log-auth-token="suspendedSession?.token?.access_token || ''"
        data-testid="auth-legacy-mfa-enrollment"
        @completed="handleMfaSetupCompleted"
        @phone-captured="handleMfaSetupPhoneCaptured"
        @skip="handleMfaSetupSkip" />
    </div>
    <div v-else class="h-full flex flex-col">
      <atoms-alert
        v-if="newAccountWarning"
        size="p1"
        type="warning"
        has-close
        :full-width="true"
        class="mb-6"
      >
        <strong>Hmm, we couldn't find an account with that email.</strong><br />
        Double-check your email and
        <span
          class="text-semantic-surface-on-surface underline font-bold cursor-pointer"
          @click="resetForm"
        >try again</span> or
        register a new account.
      </atoms-alert>
      <h1 class="text-semantic-surface-on-surface text-xl" :class="{'text-center':pwaInstalled}">
        <img
          width="110"
          height="32"
          src="/images/clubplus-dark.svg"
          alt="Club Plus Logo"
        />
      </h1>

      <!-- HEADERS AND SUBTEXTS -->
      <template v-if="!isLoginAttempt && !isRegistrationAttempt">
        <div class="mt-6 mb-2 font-bold text-2xl text-left">
          Sign in or create account
        </div>
        <div class="mb-4 text-base text-left font-normal text-gray-700">
          Choose how you’d like to continue, we’ll guide you through the rest.
        </div>
      </template>
      <template v-else-if="isLoginAttempt">
        <div class="mt-6 mb-2 font-bold text-2xl text-left">
          Welcome back!
        </div>
        <div class="mb-4 text-base text-left font-normal text-gray-700">
          We found your account. Enter your password to log in.
        </div>
      </template>
      <template v-else-if="isRegistrationAttempt">
        <div class="mt-6 mb-4 font-bold text-2xl text-left">
          Create your free account
        </div>
        <ul class="mb-4 space-y-3 md:hidden">
          <li
            v-for="benefit in registrationBenefits"
            :key="benefit.lead"
            class="flex items-start text-left"
          >
            <span class="mr-2 mt-0.5 text-[#8e3cf7]">
              <V2IconsSuccessRegular />
            </span>
            <span class="text-sm text-semantic-surface-on-surface">
              <strong>{{ benefit.lead }},</strong> {{ benefit.rest }}
            </span>
          </li>
        </ul>
      </template>

      <form @submit.prevent="authStepsHandler">
        <div class="grid grid-cols-1 gap-4">
          <template v-for="input in schema" :key="input.name">
            <div>
              <!-- Password label -->
              <label
                :for="input.id"
                class="block mb-2 text-semantic-surface-on-surface sm:text-base text-sm font-bold"
              >
                {{input.label}}
              </label>
              <!-- Password input -->
              <V2AtomsInput
                v-model="model[input.name]"
                :validate="validateKey"
                v-bind="input"
              />
              <!-- Forgot password link: shown by default under email on the initial screen and under password during login -->
              <div
                v-if="(isLoginAttempt && input.name === 'password') || (!isLoginAttempt && !isRegistrationAttempt && input.name === 'email')"
                class="mt-1 mb-2 text-left"
              >
                <nuxt-link
                  class="!text-semantic-primary-default underline text-base font-semibold"
                  :to="generateForgotPasswordPageUrl()"
                >
                  Forgot your password?
                </nuxt-link>
              </div>
            </div>
            <MoleculesFormPasswordMeter
              v-if="input.passwordMeter"
              :schema-obj="input"
              :force-poor-status="forcePoorStatus"
              :password="model[input.name] || ''"
              label="Your password must contain:"
            />
          </template>
        </div>

        <div
          v-if="registrationError"
          role="alert"
          data-testid="auth-register-error-alert"
          class="mt-4 w-full rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {{ registrationError }}
        </div>

        <div class="grid gap-4 mt-4" :class="{'grid-cols-1 whitespace-nowrap':isRegistrationAttempt, 'grid-cols-1':!isRegistrationAttempt}">
          <v2-atoms-button
            v-for="btn in formButtons"
            :key="btn.action"
            v-bind="btn"
            :disabled="!isButtonReady"
            :enable-tracking="isRegistrationAttempt"
          >
            {{ btn.label }}
          </v2-atoms-button>
          <!-- Already a member? Login -->
          <p v-if="isRegistrationAttempt " class="mt-2  mb-2 text-center text-md">
            Already a member?
            <v2-atoms-button
              id="auth_register_btn"
              class="p-0 !text-base !inline-flex"
              size="small"
              theme="tertiary"
              enable-tracking
              @click="redirectionHandler($event, generateLoginPageUrl())"
            >
              Login
            </v2-atoms-button>
          </p>
        </div>
      </form>

      <template v-if="isRegistrationAttempt">
        <p class="w-full text-center text-sm border-b border-solid border-gray-400 leading-[0.1em] mt-8 mb-5">
          <span class="bg-white px-12 text-semantic-surface-on-surface font-normal">
            OR
          </span>
        </p>

        <div class="flex justify-center gap-4 mb-4">
          <button
            v-for="sso in activeSSOItems"
            :id="`auth-register-social-${getSsoKey(sso)}-btn`"
            :key="sso"
            :data-testid="`register-${getSsoKey(sso)}-btn`"
            type="button"
            :aria-label="`Continue with ${getSsoLabel(sso)}`"
            class="flex h-12 w-12 items-center justify-center rounded-lg border border-semantic-outline-variant bg-semantic-surface-default transition-colors hover:bg-semantic-surface-container-high disabled:opacity-50"
            :disabled="!isButtonReady"
            @click="socialAuthInit({ social_auth_type: sso })"
          >
            <img
              :src="getSsoIcon(sso)"
              width="32"
              height="32"
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>
      </template>

      <template v-else>
        <p class="w-full text-center text-sm border-b border-solid border-gray-400 leading-[0.1em] mt-8 mb-5">
          <span class="bg-white px-12 text-semantic-surface-on-surface font-normal">
            or
          </span>
        </p>

        <div class="flex justify-center gap-4 mb-4">
          <button
            v-for="sso in activeSSOItems"
            :id="`auth-social-${getSsoKey(sso)}-btn`"
            :key="sso"
            :data-testid="`login-${getSsoKey(sso)}-btn`"
            type="button"
            :aria-label="`Continue with ${getSsoLabel(sso)}`"
            class="flex h-12 w-12 items-center justify-center rounded-lg border border-semantic-outline-variant bg-semantic-surface-default transition-colors hover:bg-semantic-surface-container-high disabled:opacity-50"
            :disabled="!isButtonReady"
            @click="socialAuthInit({ social_auth_type: sso })"
          >
            <img
              :src="getSsoIcon(sso)"
              width="32"
              height="32"
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>
      </template>

      <!-- Not member yet? Create an Account -->
      <p v-if="isLoginAttempt " class="mt-4 mb-4 text-center text-sm">
        Not a member?

        <v2-atoms-button
          id="auth_register_btn"
          class="p-0 !text-base !inline-flex"
          size="small"
          theme="tertiary"
          enable-tracking
          @click="redirectionHandler($event, generateRegisterPageUrl())"
        >
          Create an Account
        </v2-atoms-button>
      </p>

      <p v-if="!pwaInstalled" class="m-4 mb-4 text-sm font-normal text-center text-semantic-surface-on-surface">
        <template v-if="authRoute === 'login' && !hideAuthFooterLink">
          Not a member?
          <v2-atoms-button
            id="auth_register_btn"
            class="p-0 !text-base !inline-flex"
            size="small"
            theme="tertiary"
            enable-tracking
            @click="redirectionHandler($event, generateRegisterPageUrl())"
          >
            Join the Club
          </v2-atoms-button>
        </template>
        <template v-if="authRoute === 'register' && !hideAuthFooterLink">
          Already a member?
          <v2-atoms-button
            id="auth_login_btn"
            class="p-0 mb-4 !text-base !inline-flex"
            size="small"
            theme="tertiary"
            enable-tracking
            @click="redirectionHandler($event, generateLoginPageUrl())"
          >
            Login
          </v2-atoms-button>
        </template>
      </p>
      <div
        v-if="isRegistrationAttempt"
        class="font-normal text-semantic-surface-on-surface-variant text-semantic-sizing-label text-center px-0 pb-6"
        :class="{'mt-auto':pwaInstalled}"
      >
        By clicking <strong class="text-semantic-surface-on-surface-variant">'Create an Account'</strong>, I acknowledge that I have read and agree to the
        <nuxt-link
          ref="terms_of_use"
          class="underline cursor-pointer text-semantic-surface-on-surface-variant"
          to="/terms-of-use/"
          target="_blank"
        >
          <strong>Terms of Use</strong>
        </nuxt-link>, the
        <nuxt-link
          ref="privacy_policy"
          to="/privacy-policy/"
          target="_blank"
          class="underline cursor-pointer text-semantic-surface-on-surface-variant"
        >
          <strong>Privacy Policy</strong>
        </nuxt-link> and the <br>
        <nuxt-link
          ref="collection_notice"
          to="/collection-notice/"
          target="_blank"
          class="underline cursor-pointer text-semantic-surface-on-surface-variant"
        >
          <strong>Collection Notice</strong>
        </nuxt-link>.
      </div>
      <div
        v-if="isLoginAttempt || pwaInstalled && !isRegistrationAttempt"
        class="font-normal text-surface-on-surface-variant text-xs text-center px-0 pb-6 leading-[1.125rem] sm:hidden mt-4"
      >
        © 2025 Compare Club. All Rights Reserved<br/>
        <nuxt-link
          ref="terms_of_use"
          class="underline cursor-pointer"
          to="/terms-of-use/"
          target="_blank"
        >
          <strong class="font-normal text-surface-on-surface-variant">Terms of Use</strong>
        </nuxt-link> &
        <nuxt-link
          ref="privacy_policy"
          to="/privacy-policy/"
          target="_blank"
          class="underline cursor-pointer"
        >
          <strong class="font-normal text-surface-on-surface-variant">Privacy Policy</strong>
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import memberhubConstants from '~/constants/memberhub.constants'
import { passwordValidator, emailValidator } from '~/helpers/validator'
import { generateForgotPasswordPageUrl, generateRegisterPageUrl } from '~/utils/helpers'
import isFlagOn from '~/composables/feature-flag'
import { FEATURE_FLAG } from '~/constants/feature-flags.constants'
import isDescopeMfaEnabled from '~/composables/use-descope-mfa-flag'
import useForceMfaFlag from '~/composables/use-force-mfa-flag'
import useMfaBypassFlag from '~/composables/use-mfa-bypass-flag'
import usePostRegistrationMfa from '~/composables/use-post-registration-mfa'
import { setSmsMfaPreference } from '~/utils/descope/mfa-preferences'
import { DESCOPE_ERROR_MAX_OTP_ATTEMPTS, DESCOPE_ERROR_NETWORK, DESCOPE_ERROR_UNVERIFIED_PHONE, isOtpSendRateLimited } from '~/composables/use-descope-web-auth'
import useOtpSendLockout, { DEFAULT_LOCKOUT_SECONDS } from '~/composables/use-otp-send-lockout'
import useMfaLogging, { MFA_EVENT, MFA_CONTEXT, MFA_TRIGGER } from '~/composables/use-mfa-logging'
import { isUsablePhone } from '#shared/phone'
import { Capacitor } from '@capacitor/core'

defineOptions({
  name: 'OrganismsAuthentication'
})
const pwaInstalled = usePwaInstalled()
const forcePoorStatus = ref(false)
const registrationError = ref('')

// Governs which system owns REGISTRATION. Off means Member Hub registers the user and
// Descope is reached only afterwards, for MFA — see registerUser().
const useDescopeRegistration = ref(false)

// --- SMS second-factor challenge state (legacy login path) ---
const mfaFeatureEnabled = ref(false)
const mfaChallengeActive = ref(false)
const maskedPhone = ref('')
const mfaError = ref('')
const isMfaSubmitting = ref(false)
const mfaSendLockout = useOtpSendLockout()
const { logMfaEvent } = useMfaLogging()
// Descope refresh token backing the in-flight challenge, captured before the primary
// session is parked so a resend still works while nothing is committed.
const mfaRefreshToken = ref('')
/*
 * Shared with NavBar, which mounts the auth modal behind `!userLoggedIn`. Once login
 * commits the session that turns true and the modal — with whatever MFA step it is showing
 * — is unmounted outright. The OTP challenge survived only incidentally, because it parks
 * the session; enrolment does not, so it was destroyed the moment it appeared. This keeps
 * the modal mounted for as long as an MFA step is on screen.
 */
const mfaStepActive = useState('auth:mfa-step-active', () => false)

// Set once a correct code is accepted, so the OTP form stays disabled while the redirect
// runs instead of briefly becoming interactive again.
const mfaVerified = ref(false)

// --- inline enrolment state (auth modal only) ---
const mfaSetupActive = ref(false)
const mfaSetupRefreshToken = ref('')
// false for registration (always mandatory, hardcoded); !forceMfa for the login-guard case
// below — set explicitly at each startMfaSetup() call site, never both at once.
const mfaSetupAllowSkip = ref(false)
// What to run once enrolment is completed or skipped — the two entry points (registration
// and an unenrolled login) resume differently.
const mfaSetupContinuation = ref(null)

// The Member Hub session held back until the second factor passes — see runMfaGate.
const suspendedSession = ref(null)
const pendingRedirect = ref(null)
// Descope's live enrolment state for this account, carried from the gate through to the
// profile write that happens once the challenge is done.
const descopeSecurityStatus = ref(null)
/*
 * Descope's own phone (E.164, real country code), already fetched in runMfaGate, wins ONLY
 * once Descope reports it verified — this component only ever reaches enrolment when it did
 * NOT (see runMfaGate's verifiedPhone branch), so this falls back to Member Hub's copy in
 * practice, but the explicit check keeps this consistent with the other two enrolment
 * surfaces (TwoStepVerification, setup-mfa) rather than relying on that context.
 *
 * Member Hub's `phone_number` is stored with no dialling code at all (see shared/phone.ts),
 * an AU-only simplification that silently corrupts any other country's number, so it is
 * only trustworthy as a fallback prefill — never as the verified source of truth.
 */
const mfaSetupInitialPhone = computed(() => (isUsablePhone(descopeSecurityStatus.value?.phone) && descopeSecurityStatus.value?.verifiedPhone)
  ? descopeSecurityStatus.value.phone
  : (userStore.getUserProfile?.phone_number || ''))
const { $tracking, $bus, $toast } = useNuxtApp()
const route = useRoute()
const { getStrongPasswordRequirementMessage } = useStrongPasswordRequirementMessage()
const { signUpWithPassword, getSecurityStatus, sendSmsMfa, verifySmsMfa } = useDescopeWebAuth()
const { descopeAccessToken, descopeRefreshToken, hasDescopeSession, ensureDescopeSession, storeDescopeToken } = useDescopeSession()

const userStore = useUserStore()
const forceMfa = useForceMfaFlag()
const mfaBypass = useMfaBypassFlag()
const { resolvePostRegistrationPath } = usePostRegistrationMfa()
const props = defineProps({
  authRoute: {
    type: String,
    required: true,
    validator (val) {
      return ['login', 'register'].includes(val)
    }
  },
  compact: {
    type: Boolean,
    default: true
  },
  /*
   * Set by MemberhubModalAuthentication. The existing `modalAuth` computed infers the modal
   * from the route, which only covers the quote-results page — the modal is global and can
   * be opened from anywhere via the `mh-auth` event, so MFA needs a signal that is actually
   * true wherever it renders.
   */
  inModal: {
    type: Boolean,
    default: false
  },
  dataTestid: {
    type: String,
    default: 'organisms-authentication'
  }
})

const emits = defineEmits(['reset-form', 'user-validation','ps-registration'])

const isButtonReady = ref(false)
const newAccountWarning = ref(false)
const hideAuthFooterLink = ref(false)
const enableAppleSSO = ref(false)

const schema = ref([
  {
    id: 'email',
    name: 'email',
    type: 'text',
    theme: 'light',
    placeholder: '',
    label: 'Email',
    isSuccess:true,
    darkPlaceholder:true,
    modelType: 'text',
    hasPercentage: false,
    hasCurrency: false,
    autocomplete: 'email',
    errorMsg: 'Please enter a valid email address.'
  }
])

const model = ref({
  email: null
})
const isLoginAttempt = ref(false)
const isRegistrationAttempt = ref(false)
const registrationBenefits = [
  { lead: 'Know when to switch', rest: 'we\'ll tell you the best time.' },
  { lead: 'See how much you could save', rest: 'tailored to you.' },
  { lead: 'Everything in one place', rest: 'all quotes and policies.' }
]
const registrationSchema = ref([
  {
    name: 'given_name',
    type: 'text',
    theme: 'light',
    placeholder: '',
    label: 'First Name',
    isSuccess:true,
    darkPlaceholder:true,
    modelType: 'text',
    hasPercentage: false,
    hasCurrency: false,
    autocomplete: 'given-name'
  },
  {
    name: 'family_name',
    type: 'text',
    theme: 'light',
    placeholder: '',
    label: 'Family Name',
    isSuccess:true,
    darkPlaceholder:true,
    modelType: 'text',
    hasPercentage: false,
    hasCurrency: false,
    autocomplete: 'family-name'
  },
  {
    name: 'password',
    type: 'password',
    theme: 'light',
    placeholder: '',
    label: 'Password',
    isSuccess:true,
    darkPlaceholder:true,
    modelType: 'text',
    hasPercentage: false,
    hasCurrency: false,
    autocomplete: 'new-password',
    passwordMeter: true
  }
])
const registrationModel = ref({
  given_name: null,
  family_name: null,
  password: null
})
const formButtons = computed(() => {
  if (isLoginAttempt.value) {
    return [
      {
        id: 'auth_login_submit_btn',
        type: 'submit',
        action: 'submit',
        size: 'base',
        full: true,
        label: 'Log In'
      }
    ]
  }
  if (isRegistrationAttempt.value) {
    return [
      {
        id: 'auth_register_submit_btn',
        type: 'submit',
        action: 'submit',
        size: 'base',
        full: true,
        label: 'Create an Account'
      }
    ]
  }
  return [
    {
      id: 'auth_email_submit_btn',
      type: 'submit',
      action: 'submit',
      size: 'base',
      full: true,
      label: 'Continue'
    }
  ]
})

const isLoginPage = computed(() => {
  return props.authRoute === 'login'
})

const activeSSOItems = computed(() => {
  const baseItems = [memberhubConstants.GOOGLE_AUTH, memberhubConstants.FACEBOOK_AUTH]

  if (enableAppleSSO.value) {
    return [...baseItems, memberhubConstants.APPLE_AUTH]
  }

  return baseItems
})

// Icon-only square SSO buttons — mirrors the style used on the Descope login screen
// (AuthenticationDescopeWeb) rather than AtomsButtonSSOTile's full-width colored-background
// tiles, per the simplified sign-up design.
function getSsoKey (ssoType) {
  return getSsoLabel(ssoType).toLowerCase()
}

function getSsoLabel (ssoType) {
  switch (ssoType) {
    case memberhubConstants.GOOGLE_AUTH:
      return 'Google'
    case memberhubConstants.FACEBOOK_AUTH:
      return 'Facebook'
    case memberhubConstants.APPLE_AUTH:
      return 'Apple'
    default:
      return 'Google'
  }
}

function getSsoIcon (ssoType) {
  switch (ssoType) {
    case memberhubConstants.GOOGLE_AUTH:
      return '/icons/social/google.svg'
    case memberhubConstants.FACEBOOK_AUTH:
      return '/icons/social/facebook-square.svg'
    case memberhubConstants.APPLE_AUTH:
      return '/icons/social/apple-black.svg'
    default:
      return '/icons/social/google.svg'
  }
}

const modalAuth = computed(() => {
  return route.name === 'health-insurance-quotes-results'
})

/*
 * Whether this form is rendered inside the auth modal, for MFA purposes.
 *
 * Deliberately separate from `modalAuth` above, which is left exactly as it was: that one
 * also drives redirect and footer behaviour, and widening it would change those on every
 * page the modal can open from. This is used only by the MFA paths, which must enrol inline
 * rather than navigate away wherever the modal is showing.
 */
const inAuthModal = computed(() => props.inModal || modalAuth.value)

/*
 * Email prefill handler
 * This method prefills an email coming from route query or authModuleParams
 */
function prefillEmail () {
  const params = userStore.authModuleParams || {}
  const tmpEmail = route.query.email || params.email
  if (tmpEmail && !model.value.email) {
    model.value.email = tmpEmail
  }
  if (params.given_name && model.value.given_name !== undefined && !model.value.given_name) {
    model.value.given_name = params.given_name
  }
  if (params.family_name && model.value.family_name !== undefined && !model.value.family_name) {
    model.value.family_name = params.family_name
  }
}
const vaultStore = useVaultStore()

/*
 * Mirrors persistDescopeProfile() in AuthenticationDescopeWeb: writes Descope's own
 * enrolment state onto the stored profile (and into the local SMS preference) so the
 * account-settings two-step card can read it back.
 *
 * Without this the profile has no `descope` block at all after a legacy login, so
 * isPhoneVerified is false and the card reports two-step as off for a user who is in fact
 * enrolled and was just challenged.
 */
/*
 * Descope puts its errorDescription (falling back to errorMessage) into error.message via
 * toAuthError, so this is the text Descope returned. Raised as a toast as well as inline:
 * the challenge failing during runMfaGate has no inline surface at all — it throws, login()
 * rejects, and nothing renders it — so without this the login simply stalls.
 */
function reportDescopeError (message) {
  $toast.error(message)
  return message
}

function persistDescopeProfile (descopeProfile) {
  const profile = userStore.getUserProfile || {}

  if (descopeProfile.smsMfaEnabled !== undefined) {
    setSmsMfaPreference(profile.email, descopeProfile.smsMfaEnabled)
  }

  userStore.setProfile({
    ...profile,
    descope: {
      ...(profile.descope || {}),
      ...descopeProfile
    }
  })
}

/*
 * Runs the login request, with the SMS second factor wired in via login()'s
 * onAuthenticated gate so the challenge happens before the user is sent anywhere.
 */
function handleLoginAttempt () {
  const skipRedirect = !!userStore.authModuleParams?.skipAuthFooter
  const email = model.value.email
  const password = model.value.password

  /*
   * Claim the modal BEFORE the request, not once a step appears.
   *
   * userStore.login() commits the token and profile as soon as credentials pass, which
   * flips NavBar's `userLoggedIn` and unmounts the modal — while profile() is still in
   * flight and well before the MFA gate runs. Waiting for mfaChallengeActive/mfaSetupActive
   * to turn on would therefore set the flag on an already-destroyed component. Held for the
   * whole round trip instead, and released below if no step turned out to be needed.
   */
  if (inAuthModal.value && mfaFeatureEnabled.value) mfaStepActive.value = true

  return userStore.login({
    formData: { email, password },
    schema: schema.value,
    preventRedirect: skipRedirect,
    onAuthenticated: () => runMfaGate({ email, password, skipRedirect })
  })
    .then(() => {
      /*
       * Held back while either MFA step is on screen. MemberhubModalAuthentication closes
       * the auth modal on 'authenticated', so emitting it here would tear the modal down
       * before the challenge could be shown (login no longer offers enrolment inline —
       * only registration does, via its own finishMfaSetup/validatedUserRedirectionHandler
       * continuation). handleMfaVerify emits it once the challenge is done.
       */
      if (!mfaChallengeActive.value && !mfaSetupActive.value) {
        mfaStepActive.value = false
        $bus.emit('authenticated')
      }
    })
    .catch(error => {
      // Never leave the modal pinned open after a failed login.
      mfaStepActive.value = false
      return Promise.reject(error)
    })
}

/*
 * Opens the inline enrolment step and remembers how to resume afterwards.
 *
 * Used only in the auth modal. Everywhere else an unenrolled user is routed to
 * /dashboard/setup-mfa/, which is the better experience on a full page but would abandon a
 * part-completed quote funnel here.
 */
function startMfaSetup (continuation) {
  mfaSetupRefreshToken.value = descopeRefreshToken.value
  mfaSetupContinuation.value = continuation
  mfaSetupActive.value = true
}

/*
 * Runs the continuation without tearing the enrolment step off screen first — same reason
 * as handleMfaVerify: dropping back to the login form for a frame before the redirect
 * lands looks like the login screen flashing up again. The modal pin is released by hand
 * so the modal can still close.
 */
function finishMfaSetup () {
  const continuation = mfaSetupContinuation.value
  mfaSetupContinuation.value = null
  mfaSetupRefreshToken.value = ''
  mfaStepActive.value = false
  continuation?.()
}

/*
 * True when the modal should offer enrolment: MFA is on and a Descope session can authorize
 * the enrolment calls. Registration is unconditionally mandatory now — no stored-preference
 * carve-out — so there is nothing else to check here.
 */
function shouldOfferMfaSetup () {
  return mfaFeatureEnabled.value && hasDescopeSession.value
}

/*
 * Journey logging for this modal's inline challenge. The session is parked in the local
 * `suspendedSession` ref here — NOT in userStore.pendingMfaChallenge, which only the
 * standalone challenge page uses — so the token the interceptor cannot see has to come
 * from there. Read fresh each call: the ref is cleared on cancel/completion.
 */
function logChallengeEvent (event, detail = {}) {
  logMfaEvent(event, {
    context: MFA_CONTEXT.LOGIN_CHALLENGE,
    token: suspendedSession.value?.token?.access_token,
    ...detail
  })
}

// Persist the number as soon as the code is sent, unverified — a user who skips or drops
// out mid-flow should not have to retype it next time.
async function handleMfaSetupPhoneCaptured (phone) {
  if (!phone || userStore.getUserProfile?.phone_number === phone) return

  try {
    await userStore.saveUserProfileDetails({ formData: { phone_number: phone }, showToast: false })
  } catch {
    // Best-effort — verification can still complete even if this save fails.
  }
}

async function handleMfaSetupCompleted ({ token, phone }) {
  // Routed through storeDescopeToken so a Member Hub primary session is not swapped out
  // for a Descope one — the profile save below authenticates with it.
  storeDescopeToken(token)

  // Restores the session parked above for mandatory enrolment — the profile save just
  // below needs a live, authenticated session to succeed. A no-op when enrolment was only
  // offered (nothing was ever suspended).
  if (suspendedSession.value) {
    userStore.restoreAuthSession(suspendedSession.value)
    suspendedSession.value = null
  }

  /*
   * Refresh Member Hub's profile from Descope via /profile/sync — same as setup-mfa.vue's
   * completion — so the profile matches Descope's now-verified state. The phone_number
   * patch runs only if the sync fails, so the verified number still reaches Member Hub.
   * Best-effort either way; the factor is enrolled at Descope regardless.
   */
  const profileBeforeSave = userStore.getUserProfile || {}
  try {
    await userStore.syncProfileWithDescope()
  } catch {
    if (phone && profileBeforeSave.phone_number !== phone) {
      try {
        await userStore.saveUserProfileDetails({ formData: { phone_number: phone }, showToast: false })
      } catch {
        // Best-effort; the factor is enrolled at Descope either way.
      }
    }
  }

  /*
   * Merge the pre-sync profile back over whatever the sync (or fallback save) left in the
   * store. Both persist their response verbatim, so a partial response replaces the
   * stored profile with a partial one — and losing `email` stops setProfile's same-user
   * guard preserving the descope block on the next refetch. A no-op when the response is
   * complete.
   */
  userStore.setProfile({ ...profileBeforeSave, ...(userStore.getUserProfile || {}) })

  persistDescopeProfile({ phone, verifiedPhone: true, smsMfaEnabled: true })
  finishMfaSetup()
}

function handleMfaSetupSkip () {
  persistDescopeProfile({ smsMfaEnabled: false })

  // Restores the session parked for the login-guard case below — a no-op for registration,
  // which never suspends in the first place (its skip is always hidden, so this path is
  // never reachable there).
  if (suspendedSession.value) {
    userStore.restoreAuthSession(suspendedSession.value)
    suspendedSession.value = null
  }

  finishMfaSetup()
}

/*
 * Second-factor gate for the legacy Member Hub login path.
 *
 * Gated on feature_DescopeMFA ONLY — deliberately not on feature_DescopeAuthFlows — so
 * enrolment and challenge are available to a user signing in on this screen whether or
 * not the Descope login flow is switched on.
 *
 * Returns undefined to let login() redirect normally, false to take over navigation, or
 * a { path, query } override. See the gate contract in stores/user/actions.js.
 */
async function runMfaGate ({ email, password, skipRedirect }) {
  if (!mfaFeatureEnabled.value) return undefined

  // Dedicated test accounts (CLUB-950): no challenge, no enrolment offer. Before
  // ensureDescopeSession() so they don't even pay for the Descope sign-in.
  if (mfaBypass.value) {
    logMfaEvent(MFA_EVENT.BYPASSED, { context: MFA_CONTEXT.LOGIN_CHALLENGE })
    return undefined
  }

  /*
   * Member Hub may or may not hand back Descope-issued tokens, depending on how the
   * backend fronts Descope. ensureDescopeSession() reuses the primary session when it is
   * already Descope's — costing no extra round trip — and only falls back to a direct
   * Descope password sign-in when it is not.
   */
  const refreshToken = await ensureDescopeSession(email, password)

  /*
   * No Descope session could be established. Fail OPEN: without one we cannot even ask
   * whether this account has a second factor enrolled, and turning a Descope outage into a
   * total sign-in outage is the worse failure. Note this is the opposite of the policy
   * below, which applies once Descope has actually told us a factor is required.
   */
  if (!refreshToken) return undefined

  // Descope's own live enrolment state is the sole trigger for a challenge — never a
  // client-held preference, which a user could clear to skip their own second factor.
  const securityStatus = await getSecurityStatus(descopeAccessToken.value)

  descopeSecurityStatus.value = securityStatus

  if (!securityStatus.verifiedPhone) {
    persistDescopeProfile({ ...securityStatus, smsMfaEnabled: false })

    /*
     * Not enrolled. Registration has its own separate, unconditionally-mandatory enrolment
     * offer (see shouldOfferMfaSetup/authStepsHandler and use-post-registration-mfa.js) —
     * this is always a returning login, offered enrolment again with skip visibility driven
     * by Member Hub's own force_MFA flag — no client-side flag, no per-provider exemption.
     */
    const allowSkip = !forceMfa.value
    const destination = skipRedirect
      ? null
      : {
        path: userStore.getAuthUserSourcePage || '/dashboard/',
        query: userStore.getAuthUserSourcePageQuery || {}
      }

    if (!inAuthModal.value) {
      useMfaPostLoginGate().suspendForEnrollment({ email, phone: securityStatus.phone, destination, allowSkip })
      return { path: '/dashboard/setup-mfa/', query: {} }
    }

    /*
     * Inline, same reasoning as the challenge branch below — a redirect would eject the
     * user from a part-completed quote funnel. Tokenization: startMfaSetup captures the
     * refresh token BEFORE the session is suspended, so a resend/retry during this step
     * still has a token to authorize itself with once suspendAuthSession() below clears the
     * primary one.
     */
    mfaSetupAllowSkip.value = allowSkip
    startMfaSetup(() => {
      const redirect = pendingRedirect.value
      pendingRedirect.value = null
      $bus.emit('authenticated')
      if (redirect) $bus.emit('redirection', redirect)
    })
    pendingRedirect.value = destination
    suspendedSession.value = userStore.suspendAuthSession()
    return false
  }

  /*
   * A verified phone means the challenge is mandatory from here, and everything below
   * fails CLOSED.
   *
   * login() has to commit the session before this gate can run at all — profile() needs
   * the token to load. So park it immediately: without this, a user who abandoned the
   * challenge and simply navigated to /dashboard/ would be fully logged in having never
   * completed a second factor Descope said was required.
   *
   * Inside the modal the challenge stays inline, same reason as the enrolment branch
   * above — a redirect would eject the user from their quote funnel. Everywhere else it's
   * handed off to the shared /dashboard/authorization/mfa-challenge/ page, which now owns
   * the send/verify/restore steps this used to run inline.
   */
  const destination = skipRedirect
    ? null
    : {
      path: userStore.getAuthUserSourcePage || '/dashboard/',
      query: userStore.getAuthUserSourcePageQuery || {}
    }

  if (!inAuthModal.value) {
    useMfaPostLoginGate().suspendForChallenge({ email, phone: securityStatus.phone, destination })
    return { path: '/dashboard/authorization/mfa-challenge/', query: {} }
  }

  pendingRedirect.value = destination
  mfaRefreshToken.value = refreshToken
  suspendedSession.value = userStore.suspendAuthSession()

  mfaSendLockout.reset()
  try {
    const smsChallenge = await sendSmsMfa(email, refreshToken)
    maskedPhone.value = smsChallenge.maskedPhone
  } catch (error) {
    /*
     * Descope's own send lockout, not a hard failure — the account still has a verified
     * phone, sending is just paused. This can be hit on the very first send of a login
     * attempt (e.g. the lockout was accrued from resends on an earlier attempt), so it must
     * route into the same wait/retry panel a resend would get, rather than falling through
     * to the branch below, which discards the suspended session and bounces to login.
     */
    if (isOtpSendRateLimited(error)) {
      mfaSendLockout.start(Number(error.retryAfter) || DEFAULT_LOCKOUT_SECONDS)
      mfaError.value = ''
      mfaChallengeActive.value = true
      $tracking?.pageview({ title: 'MFA — Verify your identity' })
      logChallengeEvent(MFA_EVENT.CHALLENGE_SHOWN)
      logChallengeEvent(MFA_EVENT.SEND_RATE_LIMITED, { trigger: MFA_TRIGGER.AUTO })
      return false
    }

    // The send failed after Descope told us a verified phone exists — a contradictory
    // state. Discard the parked session rather than releasing it unchallenged.
    // Logged BEFORE the discard, which clears the token this call authenticates with.
    logChallengeEvent(MFA_EVENT.CODE_SEND_FAILED, {
      trigger: MFA_TRIGGER.AUTO,
      failure_reason: error?.code || 'unknown'
    })
    discardSuspendedSession()
    throw new Error(reportDescopeError(error?.code === DESCOPE_ERROR_UNVERIFIED_PHONE
      ? 'We could not verify your phone for sign-in. Please try again or contact support.'
      : (error?.message || 'We could not send your verification code. Please try again.')),
    { cause: error })
  }

  mfaError.value = ''
  mfaChallengeActive.value = true
  /*
   * A step, not a route: app.vue reports pageviews off $route.fullPath, which does not
   * change when the challenge takes over the card. Report it explicitly, keeping the real
   * path and distinguishing by title so no synthetic URL enters page reports.
   */
  $tracking?.pageview({ title: 'MFA — Verify your identity' })
  logChallengeEvent(MFA_EVENT.CHALLENGE_SHOWN)
  logChallengeEvent(MFA_EVENT.CODE_SENT, { trigger: MFA_TRIGGER.AUTO })
  return false
}

/*
 * Drops the held-back session for good. Used whenever the challenge cannot or will not be
 * completed — the user is simply not logged in at that point.
 */
function discardSuspendedSession () {
  mfaVerified.value = false
  suspendedSession.value = null
  pendingRedirect.value = null
  descopeSecurityStatus.value = null
  mfaRefreshToken.value = ''
  maskedPhone.value = ''
  mfaChallengeActive.value = false
  mfaSendLockout.reset()
  userStore.clearDescopeSession()
}

/*
 * Abandon the challenge and return to the login form, logged out.
 */
function cancelMfaChallenge (event) {
  // Plain <button>, so it reports its own click; V2AtomsButton's enable-tracking does not
  // apply. No event means a programmatic call, which is not a user action.
  if (event) $tracking?.click(event)

  // Before the discard, which clears the token this call authenticates with. Only a
  // challenge that was actually on screen counts — a programmatic call with nothing parked
  // is not an abandonment.
  if (suspendedSession.value) logChallengeEvent(MFA_EVENT.CHALLENGE_ABANDONED)

  discardSuspendedSession()
  mfaError.value = ''
  isMfaSubmitting.value = false
  model.value.password = ''
}

/*
 * "Can't access this number?" on this inline modal challenge — mirrors goToAccountRecovery
 * in the shared mfa-challenge.vue page, which the non-modal login path already redirects to.
 * Previously bound to cancelMfaChallenge, which discarded the suspended session outright and
 * left this entry point with no way into the lost-phone recovery flow at all.
 *
 * The session is suspended locally here (suspendedSession), not through
 * userStore.pendingMfaChallenge as the shared challenge page does, so this hands that same
 * suspended payload to pendingMfaRecovery directly rather than reading it back off the store.
 * descopeSession itself is untouched by suspendAuthSession() and stays live for the recovery
 * page's own Descope calls, exactly as it does for the non-modal path.
 */
function startAccountRecovery (event) {
  if (event) $tracking?.click(event)
  $tracking?.customEvent('mfa_phone_recovery_started', { entry_point: 'sms_challenge' })

  useMfaPostLoginGate().startRecoveryFromChallenge({
    suspended: suspendedSession.value,
    email: model.value.email,
    phone: descopeSecurityStatus.value?.phone || '',
    destination: pendingRedirect.value
  })

  suspendedSession.value = null
  pendingRedirect.value = null
  mfaRefreshToken.value = ''
  mfaChallengeActive.value = false
  mfaSendLockout.reset()

  $bus.emit('redirection', { path: '/dashboard/authorization/account-recovery/' })
}

async function handleMfaVerify (otpValue) {
  if (!otpValue || isMfaSubmitting.value) return

  mfaError.value = ''
  isMfaSubmitting.value = true

  try {
    const { token } = await verifySmsMfa(model.value.email, otpValue)

    /*
     * Only the Descope side-session is upgraded to the MFA-authenticated token. The
     * primary session is reinstated exactly as Member Hub issued it — the API interceptor
     * and every Member Hub call authenticate with that, and swapping in a Descope token
     * here would change what the rest of the app is holding.
     */
    mfaVerified.value = true
    userStore.setDescopeSession(token)
    logChallengeEvent(MFA_EVENT.CHALLENGE_SUCCEEDED)
    userStore.restoreAuthSession(suspendedSession.value)

    /*
     * Refresh Member Hub's own profile record from Descope on every successful sign-in —
     * /profile/sync fetches current Descope state server-side (phone number + email/phone
     * verified status), so Member Hub never drifts from Descope. Strictly after the
     * restore above: it authenticates with the primary session. Same call the shared
     * mfa-challenge.vue page makes for the non-modal login path.
     *
     * The response is profile-shaped and applied via setProfile; the pre-sync snapshot is
     * merged back so a partial response can never drop `email` (setProfile's same-user
     * guard). Best-effort only — the user is already signed in regardless.
     */
    const profileBeforeSync = userStore.getUserProfile || {}
    try {
      await userStore.syncProfileWithDescope()
      userStore.setProfile({ ...profileBeforeSync, ...(userStore.getUserProfile || {}) })
    } catch {
      // Best-effort only — the user is already signed in regardless of this sync.
    }

    // Strictly after the restore (and the sync above): it reinstates the profile snapshot
    // taken before the challenge, so writing the descope block any earlier would be
    // overwritten here.
    persistDescopeProfile({
      ...(descopeSecurityStatus.value || {}),
      smsMfaEnabled: true
    })

    const redirect = pendingRedirect.value
    suspendedSession.value = null
    pendingRedirect.value = null
    mfaRefreshToken.value = ''

    /*
     * mfaChallengeActive is deliberately NOT cleared here. Clearing it drops the template
     * back to the login form for a frame before the redirect lands, which reads as the
     * login screen flashing up again after a correct code. Whatever is on screen stays
     * until the navigation (or the modal closing) unmounts this component.
     *
     * mfaStepActive is released by hand for the same reason: it is normally driven by a
     * watcher on the step flags, and those no longer change here, but the modal must still
     * be free to close once 'authenticated' fires below.
     */
    mfaStepActive.value = false

    $bus.emit('authenticated')
    $bus.emit('reset-login', true)
    if (redirect) $bus.emit('redirection', redirect)
  } catch (error) {
    // Confirmed with Descope support: identical response shape to a single wrong guess —
    // this code is the only signal that the code itself is now dead and a resend is
    // required, rather than another attempt at what was just rejected.
    const isMaxAttempts = error?.code === DESCOPE_ERROR_MAX_OTP_ATTEMPTS
    // Distinct from the generic Verify button click (which fires the same way whether it
    // succeeds or fails) — mirrors the same signal added to the shared mfa-challenge.vue
    // page, for the equivalent inline challenge in this legacy modal.
    const reason = isMaxAttempts ? 'max_attempts' : (error?.code === DESCOPE_ERROR_NETWORK ? 'network' : 'invalid_code')
    $tracking?.customEvent('mfa_challenge_verify_failed', { reason })
    logChallengeEvent(isMaxAttempts ? MFA_EVENT.MAX_ATTEMPTS_EXCEEDED : MFA_EVENT.CHALLENGE_FAILED, { failure_reason: reason })
    mfaError.value = reportDescopeError(isMaxAttempts
      ? 'You\'ve used all your attempts for this code. Please request a new one.'
      : (error?.message || 'Something went wrong. Please try again.'))
  } finally {
    isMfaSubmitting.value = false
  }
}

async function handleMfaResend () {
  mfaError.value = ''
  $tracking?.customEvent('mfa_challenge_resend', {})

  try {
    const smsChallenge = await sendSmsMfa(model.value.email, mfaRefreshToken.value)
    maskedPhone.value = smsChallenge.maskedPhone
    mfaSendLockout.reset()
    logChallengeEvent(MFA_EVENT.CODE_SENT, { trigger: MFA_TRIGGER.RESEND })
  } catch (error) {
    if (isOtpSendRateLimited(error)) {
      logChallengeEvent(MFA_EVENT.SEND_RATE_LIMITED, { trigger: MFA_TRIGGER.RESEND })
      mfaSendLockout.start(Number(error.retryAfter) || DEFAULT_LOCKOUT_SECONDS)
      return
    }
    // A retry after the cooldown expired can still fail for an unrelated reason — the
    // blocked panel must not keep rendering (hiding this error) once Descope itself has
    // moved past the lockout.
    mfaSendLockout.reset()
    logChallengeEvent(MFA_EVENT.CODE_SEND_FAILED, {
      trigger: MFA_TRIGGER.RESEND,
      failure_reason: error?.code || 'unknown'
    })
    mfaError.value = reportDescopeError(error?.message || 'We could not resend your code. Please try again.')
  }
}

/*
 * Handles the authentication steps based on the user's current action.
 * This function determines whether the user is attempting to log in, register, or verify purchase status.
 * For login and registration attempts, it calls the respective methods in the `userStore` and emits an 'authenticated' event upon success.
 * If neither login nor registration is being attempted, it checks if the user has previously made a purchase using their email.
 * If the purchase is verified, it emits a 'ps-registration' event with the user's name and email.
 * If the user's purchase cannot be verified or an error occurs, it calls `userExistsHandler` to handle the next steps.
 *
 * @param {Object} payload - The data payload for the authentication step, which varies based on the action (login, registration, etc.).
 */
function authStepsHandler () {
  if(!validateForm()) return
  if (isLoginAttempt.value) {
    handleLoginAttempt()
  } else if (isRegistrationAttempt.value) {
    registrationError.value = ''
    if (inAuthModal.value && mfaFeatureEnabled.value) mfaStepActive.value = true

    registerUser()
      .then(() => {
        /*
         * Modal registration sends the user straight back to the funnel page they came from,
         * so the setup-mfa hand-off that covers full-page registration
         * (validatedUserRedirectionHandler) never runs. Offer enrolment inline instead,
         * before returning them to the funnel.
         */
        if (inAuthModal.value && shouldOfferMfaSetup()) {
          mfaSetupAllowSkip.value = false
          startMfaSetup(validatedUserRedirectionHandler)
          return
        }

        mfaStepActive.value = false
        validatedUserRedirectionHandler()
      })
      .catch(error => {
        mfaStepActive.value = false
        handleRegistrationError(error)
      })
  } else {
    vaultStore.checkPurchasedUser({ email: model.value.email })
      .then(res => {
        if(res.is_valid) {
          emits('ps-registration', { name:res.given_name,email:model.value.email })
        } else {
          userExistsHandler(model.value.email)
        }
      })
      .catch(() => {
        userExistsHandler(model.value.email)
      })
  }
}
/*
 * Handles the existence check of a user by their email.
 * This function calls `userStore.checkUserExistence` with the provided email to check if the user exists.
 * Upon receiving a response, it forwards the result to `authTypesHandler` to determine the next steps based on the user's existence.
 * If an error occurs during the check, it calls `authTypesHandler` without any arguments, indicating a fallback or error state.
 *
 * @param {string} email - The email address to check for an existing user.
 */
function userExistsHandler (email) {
  userStore.checkUserExistence(email)
    .then(res => {
      authTypesHandler(res.authentication, email)
    })
    .catch(error => {
      if (parseInt(error?.code) === 404) {
        // User does not exist — route to registration
        authTypesHandler(undefined, email)
      } else {
        // Network/server error — show toast notification
        $toast.error('Something went wrong. Please try again.')
      }
    })
}

/*
 * Authentication type handler with respect to email check response
 * This method checks the auth type and handles / passes user to desired authentication
 * @param authType
 */
function authTypesHandler (authType, email = null) {
  switch (authType) {
    case 'password':
      hideAuthFooterLink.value = true
      isLoginAttempt.value = true
      isRegistrationAttempt.value = false
      schema.value.push({
        name: 'password',
        type: 'password',
        theme: 'light',
        placeholder: '',
        label: 'Password',
        isSuccess:true,
        darkPlaceholder:true,
        modelType: 'text',
        hasPercentage: false,
        hasCurrency: false,
        autocomplete: 'current-password',
        errorMsg: 'Please enter a valid password.'
      })
      model.value.password = null
      break
    case 'google':
      socialAuthInit({ social_auth_type: memberhubConstants.GOOGLE_AUTH, email: email })
      break
    case 'facebook':
      socialAuthInit({ social_auth_type: memberhubConstants.FACEBOOK_AUTH, email: email })
      break
    case 'apple':
      socialAuthInit({ social_auth_type: memberhubConstants.APPLE_AUTH, email: email })
      break
    case 'guest':
      userStore.guestPasswordResetHandler({ formData:{ email: model.value.email } })
      navigateTo({
        path: generateGuestWelcomeBackPageUrl(),
        query: {
          email: model.value.email
        }
      })
      break
    default:
      hideAuthFooterLink.value = true
      newAccountWarning.value = route.name === 'login'
      isLoginAttempt.value = false
      // Guard against double-appending the registration fields — the register route now
      // calls this on mount to skip the old "check if this email exists" step, so a
      // second call (e.g. re-entering an email) must not duplicate schema/model entries.
      if (!isRegistrationAttempt.value) {
        isRegistrationAttempt.value = true
        schema.value.push(...registrationSchema.value)
        model.value = { ...model.value, ...registrationModel.value }
      }
      nextTick().then(() => {
        prefillEmail()
      })
      break
  }
}

/*
 * Form reset handler
 */
function resetForm () {
  const email = model.value.email
  nextTick().then(() => {
    newAccountWarning.value = false
    model.value.email = email
    hideAuthFooterLink.value = !!modalAuth.value

    isLoginAttempt.value = false
    isRegistrationAttempt.value = false
    schema.value = [{
      name: 'email',
      type: 'text',
      theme: 'light',
      placeholder: '',
      label: 'Email',
      isSuccess:true,
      darkPlaceholder:true,
      modelType: 'text',
      hasPercentage: false,
      hasCurrency: false,
      autocomplete: 'email',
      errorMsg: 'Please enter a valid email address.'
    }]
    model.value = {
      email: null
    }
  })
}

/*
 * Normalises a path for comparison against the current route — trailing slashes and query
 * strings differ between the routes these CTAs generate and what the router reports.
 */
function isCurrentRoute (path) {
  const normalise = value => String(value || '').split('?')[0].replace(/\/+$/, '') || '/'
  return normalise(path) === normalise(route.path)
}

/*
 * The "Login" / "Create an account" CTAs point at a route, but the form can already be on
 * it: entering an unknown email on the login page switches this component to registration
 * without leaving /login, so the Login CTA then targets the page it is already on. The
 * router treats that as a no-op, nothing remounts, and the form stays stuck on register.
 * Most visible in the app, where the PWA login route is the only login page there is.
 *
 * Reset in place instead — the same thing the quote-results modal already did, and for the
 * same reason: it has nowhere to navigate to either.
 */
function redirectionHandler (e, path) {
  if (route.name === 'health-insurance-quotes-results' || isCurrentRoute(path)) {
    resetForm()
    return
  }

  // Optional: $tracking comes from a client-only plugin, and a blocked or failed analytics
  // script must not stop the user navigating.
  $tracking?.click(e, path)
  $bus.emit('redirection', { path })
}

/*
 * SSO initialize
 * @param payload
 */
function socialAuthInit (payload) {
  if(Capacitor.isNativePlatform()){
    payload = { ...payload, origin: 'ios' }
  }
  userStore.socialAuthInit(payload)
}

/*
 * Registration entry point. Which system owns the registration follows
 * feature_DescopeAuthFlows, exactly as login does — the flag is a genuine kill switch, so
 * turning it off means no Descope call owns the sign-up.
 *
 * MFA does NOT follow that flag. Both paths finish with a Descope session available, so
 * the post-registration /dashboard/setup-mfa/ hand-off works in either mode; only
 * feature_DescopeMFA decides whether enrolment is actually offered.
 */
function registerUser () {
  return useDescopeRegistration.value ? registerViaDescope() : registerViaMemberHub()
}

/*
 * Legacy path: Member Hub owns the registration and signs the user in (userStore.register
 * calls login() itself with preventRedirect), leaving a Member Hub session.
 *
 * A Descope session is then obtained on top of it, because MFA enrolment is implemented
 * entirely against Descope's SDK and cannot run without one. This relies on Member Hub
 * provisioning the matching Descope user during its own registration — it fronts Descope
 * as its auth layer — so the sign-in below finds an account waiting.
 *
 * ensureDescopeSession fails open (resolves to ''), so a Descope hiccup costs the user
 * their MFA enrolment prompt, never their registration. hasDescopeSession is false in that
 * case, which is exactly what use-post-registration-mfa.js checks before routing to
 * setup-mfa — the same outcome as when the MFA flag is off and the sign-in is skipped
 * altogether.
 */
async function registerViaMemberHub () {
  await userStore.register({
    formData: {
      email: model.value.email,
      given_name: model.value.given_name,
      family_name: model.value.family_name,
      password: model.value.password
    },
    schema: schema.value
  })

  /*
   * MFA is the only consumer of the Descope side-session — every useDescopeSession()
   * caller is an MFA surface — so this is skipped entirely when feature_DescopeMFA is off,
   * rather than sending the password to Descope for a session nothing will read.
   */
  if (!mfaFeatureEnabled.value) return

  await ensureDescopeSession(model.value.email, model.value.password)
}

/*
 * Surfaces a registration failure from either path. Member Hub reports field errors under
 * error.data.errors, while the Descope SDK only carries a message — both are checked so
 * one handler covers both registration modes.
 */
function handleRegistrationError (error) {
  const message = (
    error?.data?.errors?.password?.[0] ||
    error?.data?.message ||
    error?.message ||
    ''
  ).toLowerCase()

  if (message.includes('password')) {
    forcePoorStatus.value = true
    return
  }

  registrationError.value = message.includes('already')
    ? 'An account with this email already exists. Try logging in instead.'
    : 'Something went wrong. Please try again.'
}

/*
 * Registers the user directly in Descope. Member Hub only learns about this user the way
 * the password-login flow already handles it: once a valid Descope-issued token is on the
 * request, userStore.profile() is called and Member Hub creates/syncs its own record.
 */
async function registerViaDescope () {
  const { token } = await signUpWithPassword(model.value.email, model.value.password, {
    email: model.value.email,
    givenName: model.value.given_name,
    familyName: model.value.family_name
  })
  userStore.setToken(token)
  await userStore.profile({}, true)
}

/*
 * Redirection handler for button action in validated user case
 * @param e
 */
async function validatedUserRedirectionHandler () {
  const sourcePage = userStore.getAuthUserSourcePage
  const skipRedirect = userStore.authModuleParams?.skipAuthFooter
  const path = modalAuth.value ? (sourcePage || '/dashboard/') : '/dashboard/'
  const query = modalAuth.value
    ? (userStore.getAuthUserSourcePageQuery || {})
    : {}
  $bus.emit('authenticated')
  if (skipRedirect && sourcePage && route.fullPath.startsWith(sourcePage.split('?')[0])) {
    return
  }
  /*
   * Full-page registration hands off to MFA enrolment here. Inside the modal, enrolment
   * already ran inline (startMfaSetup) before this continuation, so the user goes straight
   * back to the funnel page they came from.
   */
  const destination = inAuthModal.value
    ? { path, query }
    : await resolvePostRegistrationPath({ path, query })
  $bus.emit('redirection', destination)
}

onMounted(async () => {
  await nextTick()
  isButtonReady.value = true
  enableAppleSSO.value = await isFlagOn(FEATURE_FLAG.appleSSO)
  mfaFeatureEnabled.value = await isDescopeMfaEnabled()
  // Defaults to the legacy path if the flag lookup fails, matching the login pages.
  useDescopeRegistration.value = await isFlagOn(FEATURE_FLAG.descopeLogin).catch(() => false)

  if (!modalAuth.value && isLoginPage.value) {
    userStore.authModuleParams = {}
  }

  prefillEmail()
  if (route.name === 'health-insurance-quotes-results' || userStore.authModuleParams?.skipAuthFooter) {
    hideAuthFooterLink.value = true
  }

  /*
   * On the Descope flow, sign-up is a single, simple form — no "type your email, we'll
   * check if you already have an account" step first. authTypesHandler(undefined, ...) is
   * the same "no account found" branch the old flow reached once the check came back
   * empty; calling it on mount just unlocks the full registration fields immediately.
   *
   * With feature_DescopeAuthFlows off, registration goes to Member Hub instead — and the
   * existence check is a Member Hub call — so the legacy email-first step is restored.
   * Without it a returning user fills the whole form only to be told the account already
   * exists, rather than being routed to login before typing a password.
   */
  if (props.authRoute === 'register' && useDescopeRegistration.value) {
    authTypesHandler(undefined, model.value.email)
  }
})

watch(() => mfaChallengeActive.value || mfaSetupActive.value, active => {
  mfaStepActive.value = active
})

// Never leave the modal pinned open if this form is torn down mid-flow.
onBeforeUnmount(() => {
  mfaStepActive.value = false
})

watch(() => userStore.authModuleParams, params => {
  prefillEmail()
  if (params?.skipAuthFooter) {
    hideAuthFooterLink.value = true
  }
})

/*
 * Validates the entire form by iterating over each input in the schema and checking its validity.
 *
 * @returns {boolean} - Returns true if all inputs are valid, otherwise false.
 */
function validateForm () {
  let valid = true
  schema.value.forEach(input => {
    if(!validateKey({ target: { name: input.name } })){
      valid = false
    }
  })
  return valid
}

/*
 * Validates a form input based on its key and updates the input object with the validation results.
 *
 * @param {Event} e - The event object containing the target input element.
 * @returns {boolean} - Returns true if the input is valid, otherwise false.
 */
function validateKey (e) {
  const key = e?.target?.name
  const input = schema.value.find(formElement => formElement.name === key)
  input.errorMessage = ''
  input.isSuccess = false
  const rawValue = model.value[key]
  const isNameField = key === 'given_name' || key === 'family_name'
  const value = typeof rawValue === 'string' && isNameField ? rawValue.trim() : rawValue
  const nameAllowedPattern = /^[\p{L}\p{M}\s'.,-]+$/u
  const hasLetterPattern = /[\p{L}\p{M}]/u

  if (typeof rawValue === 'string' && isNameField) {
    model.value[key] = value
  }

  if (!value) {
    input.errorMessage = input.errorMsg || 'This field is required.'
    // Reset password validation state if password field is empty
    if (key === 'password' && input.passwordMeter) {
      input.error = undefined
      input.isSuccess = false
      if (input.errorObj) delete input.errorObj
    }
    return false
  } else if (isNameField && value && (!nameAllowedPattern.test(value) || !hasLetterPattern.test(value))) {
    input.errorMessage = 'Please enter a valid name'
    input.isSuccess = false
    return false
  } else if (input.passwordMeter && value) {
    if (forcePoorStatus.value) {
      forcePoorStatus.value = false
    }
    validatePassword(input, value)
  } else if (key === 'email' && value && !emailValidator(value)) {
    input.errorMessage = input.errorMsg || 'Please enter a valid email address.'
    input.isSuccess = false
  } else {
    input.isSuccess = true
  }
  return input.isSuccess
}

/*
 * Validates the password based on various criteria and updates the input object with the validation results.
 *
 * @param {Object} input - The input object that will be updated with the validation results.
 * @param {string} value - The password value to be validated.
 */
function validatePassword (input, value) {
  const result = {}

  // Check if the password meets the minimum length requirement of 8 characters
  const eightCharCheck = passwordValidator(value, /^.{8,}$/)

  // Check if the password meets the minimum length requirement of 6 characters
  const sixCharCheck = passwordValidator(value, /^.{6,}$/)

  // Check if the password contains at least one lowercase letter
  const lowercaseCheck = passwordValidator(value, /[a-z]/)

  // Check if the password contains at least one uppercase letter
  const uppercaseCheck = passwordValidator(value, /[A-Z]/)

  // Check if the password contains at least one number
  const numberCheck = passwordValidator(value, /\d/)

  // Check if the password contains at least one special character
  const specialCharCheck = passwordValidator(value, /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/)

  // Get the list of unmet password requirements
  result.passwordMeterRequires = getPasswordMeterRequirements(eightCharCheck, lowercaseCheck, uppercaseCheck, specialCharCheck, numberCheck)

  // Count the number of conditions satisfied
  const conditionsSatisfied = [lowercaseCheck, uppercaseCheck, numberCheck, specialCharCheck].filter(Boolean).length

  // Determine the password strength and validity based on the checks
  if (eightCharCheck && conditionsSatisfied === 4) {
    result.valid = true
    result.passwordMeter = memberhubConstants.PASSWORD_METER.STRONG
    result.passwordMeterRequires = []
  } else if (eightCharCheck && conditionsSatisfied >= 3) {
    result.valid = false
    result.passwordMeter = memberhubConstants.PASSWORD_METER.GOOD
  } else if (sixCharCheck && conditionsSatisfied >= 3) {
    result.valid = false
    result.passwordMeter = memberhubConstants.PASSWORD_METER.WEAK
  } else {
    result.passwordMeter = memberhubConstants.PASSWORD_METER.POOR
    result.valid = false
  }

  // Update the input object with the validation results
  input.error = {
    errorObj: result,
    status: true
  }
  input.isSuccess = result.valid
  // Show error message for any non-empty, invalid password
  if (!result.valid && value) {
    input.errorMessage = result.passwordMeter === memberhubConstants.PASSWORD_METER.GOOD
      ? getStrongPasswordRequirementMessage(result.passwordMeterRequires)
      : 'Must be a valid password.'
  }
}

/*
 * Generates a list of password meter requirements that are not met.
 *
 * @param {boolean} eightCharCheck - Indicates if the password meets the minimum length requirement.
 * @param {boolean} lowercaseCheck - Indicates if the password contains at least one lowercase letter.
 * @param {boolean} uppercaseCheck - Indicates if the password contains at least one uppercase letter.
 * @param {boolean} specialCharCheck - Indicates if the password contains at least one special character.
 * @param {boolean} numberCheck - Indicates if the password contains at least one number.
 * @returns {Array<string>} - An array of unmet password requirements.
 */
function getPasswordMeterRequirements (eightCharCheck, lowercaseCheck, uppercaseCheck, specialCharCheck, numberCheck) {
  const requirements = []
  if (!eightCharCheck) {
    requirements.push(memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.MIN_LENGTH)
  }
  if (!lowercaseCheck) {
    requirements.push(memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.LOWERCASE)
  }
  if (!uppercaseCheck) {
    requirements.push(memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.UPPERCASE)
  }
  if (!specialCharCheck) {
    requirements.push(memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.SPECIAL_CHAR)
  }
  if (!numberCheck) {
    requirements.push(memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.NUMBER)
  }
  return requirements
}

// <!-- Removed redundant export default block -->
</script>
